'use client';

import { useId as useReactId } from 'react';

/**
 * Generates a stable, unique id for use in `aria-*` attributes, `id`/`htmlFor` pairs, and similar
 * DOM-linking use cases.
 *
 * Wraps React's built-in `useId()` with a `spellbook-` prefix, so ids generated inside Spellbook
 * components can never collide with app-authored ids on the same page (React's own `useId` is
 * only unique *within* a given id namespace/tree, not against unrelated ids an app hardcodes
 * elsewhere).
 *
 * @param deterministicId - When provided, returned as-is instead of generating one — use this to
 * let a caller override the id (e.g. so it stays stable across server/client without a mismatch,
 * or so two related elements outside this hook's tree can share the same id).
 * @returns A stable id string.
 *
 * @example
 * ```tsx
 * const inputId = useId();
 * return (
 *   <>
 *     <Label htmlFor={inputId}>Name</Label>
 *     <input id={inputId} />
 *   </>
 * );
 * ```
 *
 * @example
 * ```tsx
 * // Caller-provided id — useful when the id must be predictable (e.g. for a test selector
 * // or an external anchor link) rather than generated.
 * const sectionId = useId('billing-section');
 * ```
 */
function useId(deterministicId?: string): string {
  const id = useReactId();
  return deterministicId || `spellbook-${id}`;
}

export { useId };
